import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Activity, BadgeCheck, ChevronDown, ChevronLeft, ChevronRight, CircleArrowUp, CircleHelp, Globe,
  Layers, MonitorSmartphone, MoonStar, Phone, Search, Smartphone, Sparkles, Tag as TagIcon,
  Users, UserX, Wifi,
} from 'lucide-react';
import { request } from '../../api/client';
import { cn, StatCard, EntityAvatar, DatePicker, FilterResetButton, Select, ExpandedRow, Tooltip } from '../../ui';
import type { StatTone } from '../../ui';
import { RoleBadge, roleLabel } from '../../components/RoleBadge';
import { formatIst, formatIstDate } from '../../lib/time';
import { useUrlString, useUrlNumber, useUrlDate, useClearUrlState } from '../../hooks/useUrlState';

/* ------------------------------------------------------------------ types */

type ActivityState = 'active' | 'stale' | 'never';
type VersionStatus = 'latest' | 'outdated' | 'unknown' | 'na';

interface InstallRow {
  id: string; name: string; phone: string | null; role: string; photoUrl: string | null;
  platform: string | null; platformInferred: boolean;
  appVersion: string | null; versionStatus: VersionStatus;
  deviceModel: string | null; deviceId: string | null; userAgent: string | null; ip: string | null;
  lastSeen: string | null; lastLoginAt: string | null; firstSeen: string | null; accountCreatedAt: string | null;
  sessionCount: number; deviceCount: number;
  activity: ActivityState;
}
interface ListResp {
  installs: InstallRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
  latestVersion: string | null;
}
interface RoleStat { role: string; people: number; signedIn: number; app: number; active: number }
interface Stats {
  people: number; total: number; never_signed_in: number;
  android: number; ios: number; web: number; app: number; unknown_platform: number;
  active_last_30d: number; active_last_7d: number; stale: number;
  new_last_30d: number; oldest_stale_days: number | null;
  latest_version: string | null; on_latest: number; outdated: number; unknown_version: number;
  version_distribution: { version: string | null; count: number }[];
  by_role: RoleStat[];
  devices: { model: string; count: number }[];
}

const PAGE_SIZE = 25;

/**
 * The four query keys the KPI tiles / version chips write to.
 *
 * A tile is labelled with a count, so activating it has to produce exactly that count — which it
 * cannot do while a filter clicked two tiles ago is still narrowing the list. Every drill therefore
 * CLEARS this whole set before writing its own key, and the SCOPE filters (search, role, dates) are
 * deliberately not in it: those stay, because the tiles are counted with them applied.
 */
const CATEGORY_KEYS = ['platform', 'version', 'activity', 'installed', 'page'];

/* ------------------------------------------------------------- small bits */

function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

const pct = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 100) : 0);
/** A count the API may not have sent. An em dash is the house spelling for "could not be loaded";
 *  printing `undefined` into a sentence, or defaulting it to 0, both state something untrue. */
const num = (v: number | null | undefined) => (v == null ? '—' : v);

/** Per-platform identity: one hue, one icon, one label, used by the chip AND the KPI band. */
const PLATFORM_META: Record<string, { label: string; icon: typeof Smartphone; chip: string; bar: string }> = {
  ANDROID: { label: 'Android', icon: Smartphone, chip: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300', bar: 'bg-brand-500' },
  IOS: { label: 'iOS', icon: Smartphone, chip: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300', bar: 'bg-violet-500' },
  WEB: { label: 'Web', icon: Globe, chip: 'bg-sky-500/10 text-sky-700 dark:text-sky-300', bar: 'bg-sky-500' },
};

function PlatformChip({ platform, inferred }: { platform: string | null; inferred: boolean }) {
  // "Not recorded" is a fact worth printing, not an em-dash: it is the difference between "this
  // person is on a browser" and "we have never been told what they are on", and on this page the
  // second is a gap someone can close by shipping an app build that reports its platform.
  if (!platform) {
    return (
      <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 type-caption-strong bg-warning-500/12 text-warning-700 dark:text-harvest-300" title="This session did not report a platform and its User-Agent gives no clue">
        <CircleHelp className="size-3" /> Not recorded
      </span>
    );
  }
  const m = PLATFORM_META[platform] ?? { label: platform, icon: Smartphone, chip: 'bg-surface-2 text-content', bar: 'bg-surface-3' };
  const Icon = m.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded px-2 py-0.5 type-caption-strong', m.chip)}
      title={inferred ? 'Inferred from the User-Agent — the app did not report a platform' : 'Reported by the app'}>
      <Icon className="size-3" /> {m.label}{inferred && <span className="opacity-60">~</span>}
    </span>
  );
}

function ActivityTag({ activity, lastSeen }: { activity: ActivityState; lastSeen: string | null }) {
  if (activity === 'never') {
    return <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 type-caption-strong bg-danger-500/10 text-danger-600 dark:text-danger-500"><UserX className="size-3" /> Never signed in</span>;
  }
  if (activity === 'active') {
    return <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 type-caption-strong bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"><Wifi className="size-3" /> Active</span>;
  }
  const days = lastSeen ? Math.floor((Date.now() - new Date(lastSeen).getTime()) / 86_400_000) : null;
  return (
    <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 type-caption-strong bg-warning-500/12 text-warning-700 dark:text-harvest-300">
      <MoonStar className="size-3" /> Dormant{days != null && ` ${days}d`}
    </span>
  );
}

function VersionCell({ row, latest }: { row: InstallRow; latest: string | null }) {
  if (!row.appVersion) {
    return row.versionStatus === 'unknown'
      ? <Tooltip title="On the app, but no version has been reported yet — an older build that predates version reporting."><span className="type-caption text-warning-700 dark:text-harvest-300">not reported</span></Tooltip>
      : <span className="type-caption italic text-subtle">n/a</span>;
  }
  const outdated = row.versionStatus === 'outdated';
  return (
    <div className="min-w-0">
      <span className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono type-caption-strong',
        outdated
          ? 'border-warning-500/40 bg-warning-500/10 text-warning-700 dark:text-harvest-300'
          : 'border-brand-500/40 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300')}>
        {!outdated && <BadgeCheck className="size-3" />}v{row.appVersion.replace(/^v/, '')}
      </span>
      <div className="mt-0.5 type-caption text-subtle">{outdated ? `update to v${latest}` : 'latest'}</div>
    </div>
  );
}

/** A proportional bar. Segments with no value are dropped so a 0 never draws a 1px sliver. */
function StackedBar({ segments, className }: { segments: { key: string; value: number; cls: string; label: string }[]; className?: string }) {
  const total = segments.reduce((n, s) => n + s.value, 0);
  const bar = (
    <div className={cn('flex h-2 w-full overflow-hidden rounded-pill bg-surface-3', className)}>
      {total > 0 && segments.filter((s) => s.value > 0).map((s) => (
        <div key={s.key} className={cn('h-full', s.cls)} style={{ width: `${(s.value / total) * 100}%` }} />
      ))}
    </div>
  );
  if (!total) return bar;
  // The tooltip covers the WHOLE bar rather than each segment: a segment can be a couple of pixels
  // wide (1 of 21), which is not a hover target anyone can hit, and the reader wants the split —
  // all three numbers at once — not whichever slice the pointer happened to land on. Zero segments
  // are listed too, since "never signed in: 0" is the good news and is worth stating.
  return (
    <Tooltip block label={
      <span className="flex min-w-[11rem] flex-col gap-1">
        {segments.map((s) => (
          <span key={s.key} className="flex items-center gap-2 whitespace-nowrap">
            <span className={cn('size-2 shrink-0 rounded-pill', s.cls)} />
            <span className="flex-1">{s.label}</span>
            <span className="font-semibold tabular-nums">{s.value}</span>
            <span className="w-9 text-right tabular-nums opacity-70">{pct(s.value, total)}%</span>
          </span>
        ))}
      </span>
    }>{bar}</Tooltip>
  );
}

function Panel({ title, icon, hint, children }: { title: string; icon: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col rounded-card border border-line bg-surface shadow-e1 p-3">
      <div className="mb-2.5 flex items-start justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 type-overline text-content">
          <span className="text-brand-600">{icon}</span> {title}
        </div>
        {hint && <div className="type-caption text-subtle">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="type-overline text-subtle">{label}</div>
      <div className="mt-0.5 break-words type-body text-content">{value ?? <span className="italic text-subtle">—</span>}</div>
    </div>
  );
}

/* -------------------------------------------------------------- the page */

export function AppInstallsPage() {
  // Scope filters — these narrow the list AND the KPI band.
  const [qUrl, setQUrl] = useUrlString('q');
  const [role, setRole] = useUrlString('role');
  const [from, setFrom] = useUrlDate('from');
  const [to, setTo] = useUrlDate('to');
  // Category filters — what the KPI tiles and the version chips write.
  const [platform, setPlatform] = useUrlString('platform');
  const [version, setVersion] = useUrlString('version');
  const [activity, setActivity] = useUrlString('activity');
  const [installed, setInstalled] = useUrlString('installed');
  const [page, setPage] = useUrlNumber('page', 1);
  const [sort, setSort] = useUrlString('sort', 'desc');
  const clearUrl = useClearUrlState();

  const [search, setSearch] = useState(qUrl);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setSearch(qUrl); }, [qUrl]);
  useEffect(() => {
    const t = setTimeout(() => { if (search.trim() !== qUrl) setQUrl(search.trim()); }, 350);
    return () => clearTimeout(t);
  }, [search, qUrl, setQUrl]);

  // A scope change invalidates the current page number — but only a change the USER made. Running
  // this on mount would throw away the `?page=3` in a bookmarked or Back-restored address.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setPage(1);
  }, [qUrl, role, from, to, setPage]);

  const scopeParams = useMemo(() => ({
    ...(qUrl ? { search: qUrl } : {}),
    ...(role ? { role } : {}),
    ...(from ? { from: `${from.format('YYYY-MM-DD')} 00:00:00` } : {}),
    ...(to ? { to: `${to.format('YYYY-MM-DD')} 23:59:59` } : {}),
  }), [qUrl, role, from, to]);

  const params = useMemo(() => ({
    ...scopeParams, page, limit: PAGE_SIZE, sort,
    ...(platform ? { platform } : {}), ...(version ? { version } : {}),
    ...(activity ? { activity } : {}), ...(installed ? { installed } : {}),
  }), [scopeParams, page, sort, platform, version, activity, installed]);

  const listQuery = useQuery({ queryKey: ['app-installs', params], placeholderData: keepPreviousData, queryFn: () => request<ListResp>({ url: '/app-installs', params }) });
  // The band is fetched WITHOUT the category filters it sets, so drilling one tile narrows the
  // table without collapsing its siblings to zero.
  const statsQuery = useQuery({ queryKey: ['app-installs', 'stats', scopeParams], placeholderData: keepPreviousData, staleTime: 30_000, queryFn: () => request<Stats>({ url: '/app-installs/stats', params: scopeParams }) });

  const rows = listQuery.data?.installs ?? [];
  const pg = listQuery.data?.pagination ?? { page: 1, pages: 1, total: 0, limit: PAGE_SIZE };
  const st = statsQuery.data;
  const latest = st?.latest_version ?? listQuery.data?.latestVersion ?? null;

  // Every read of a stats ARRAY goes through a defaulted local, declared before its first use.
  //
  // Not defensiveness for its own sake: an API one deploy behind returns this object with half its
  // keys missing, and `st.devices.length` on the absent one threw during render — which unmounts
  // the WHOLE app, navigation included, not just this panel. A KPI page must degrade to blank
  // numbers when a field it does not recognise is missing, never take the shell down with it.
  const deviceModels = st?.devices ?? [];
  const versionRows = st?.version_distribution ?? [];
  const roleRows = st?.by_role ?? [];
  const versionTotal = versionRows.reduce((n, v) => n + v.count, 0);
  const signedInTotal = st?.total ?? 0;
  const deviceless = st ? Math.max((st.total ?? 0) - deviceModels.reduce((n, d) => n + d.count, 0), 0) : 0;

  /**
   * Bring the register into view after a drill.
   *
   * The KPI band is at the top of the page and the table starts roughly 1,200px below it, so on a
   * 950px-tall window clicking a tile filtered a table with ZERO rows on screen — the tile tinted,
   * the URL changed, and every visible pixel stayed the same. "Nothing happens when I click a KPI"
   * was an accurate description of a filter working perfectly out of sight.
   */
  function revealResults() {
    requestAnimationFrame(() => {
      const el = resultsRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Already looking at the table — don't yank the page around under someone reading it.
      if (top >= 0 && top < window.innerHeight * 0.55) return;
      // 76px clears the sticky top nav, so the row count and the filter chips land below it
      // rather than behind it.
      window.scrollTo({ top: Math.max(top + window.scrollY - 76, 0), behavior: 'smooth' });
    });
  }

  /** Every drill replaces the category set — see CATEGORY_KEYS. */
  function drill(next: Partial<Record<'platform' | 'version' | 'activity' | 'installed', string>>) {
    clearUrl(CATEGORY_KEYS);
    if (next.platform) setPlatform(next.platform);
    if (next.version) setVersion(next.version);
    if (next.activity) setActivity(next.activity);
    if (next.installed) setInstalled(next.installed);
    setOpen(new Set());
    revealResults();
  }
  const toggle = (active: boolean, next: Parameters<typeof drill>[0]) => () => drill(active ? {} : next);

  function reset() {
    clearUrl([...CATEGORY_KEYS, 'q', 'role', 'from', 'to', 'sort']);
    setSearch('');
    setOpen(new Set());
  }

  /* ---------------------------------------------------------- KPI band */

  const neverByRole = roleRows.filter((r) => r.people > r.signedIn).sort((a, b) => (b.people - b.signedIn) - (a.people - a.signedIn));
  const gapHint = neverByRole.slice(0, 3).map((r) => { const n = r.people - r.signedIn; return `${n} ${roleLabel(r.role)}${n === 1 ? '' : 's'}`; }).join(' · ');

  type Tile = { key: string; label: string; value?: number; hint: ReactNode; tone: StatTone; icon: ReactNode; active: boolean; onClick?: () => void };
  const noCategory = !platform && !version && !activity && !installed;

  // Band 1 partitions everyone with a login: app + web + not-recorded + never = people.
  const reachTiles: Tile[] = [
    {
      // The headline counts what the UNFILTERED LIST holds, not everyone who owns a login — a tile
      // labelled 51 that opens 45 rows is exactly the defect the house rule forbids. The wider
      // figure is still here, in the hint, right beside the tile that drills it.
      key: 'people', label: 'Signed in at least once', value: st?.total, tone: 'neutral', icon: <Users size={18} />, active: noCategory,
      hint: st?.people != null ? `of ${st.people} people with a login — ${num(st.never_signed_in)} never have` : 'everyone who has opened the app or the site',
      onClick: st?.total ? toggle(noCategory, {}) : undefined,
    },
    {
      key: 'app', label: 'On the mobile app', value: st?.app, tone: 'brand', icon: <Smartphone size={18} />, active: platform === 'APP',
      hint: st ? `${num(st.android)} Android · ${num(st.ios)} iOS${st.people && st.app != null ? ` — ${pct(st.app, st.people)}% of everyone` : ''}` : 'Android + iOS',
      onClick: st?.app ? toggle(platform === 'APP', { platform: 'APP' }) : undefined,
    },
    {
      key: 'web', label: 'Browser only', value: st?.web, tone: 'sky', icon: <Globe size={18} />, active: platform === 'WEB',
      hint: 'their latest sign-in came from a web browser',
      onClick: st?.web ? toggle(platform === 'WEB', { platform: 'WEB' }) : undefined,
    },
    {
      key: 'unknown', label: 'Platform not recorded', value: st?.unknown_platform, tone: 'gold', icon: <CircleHelp size={18} />, active: platform === 'UNKNOWN',
      hint: 'signed in, but the session named no device or platform',
      onClick: st?.unknown_platform ? toggle(platform === 'UNKNOWN', { platform: 'UNKNOWN' }) : undefined,
    },
    {
      key: 'never', label: 'Never signed in', value: st?.never_signed_in, tone: 'danger', icon: <UserX size={18} />, active: activity === 'never',
      hint: gapHint || 'every account has been used at least once',
      onClick: st?.never_signed_in ? toggle(activity === 'never', { activity: 'never' }) : undefined,
    },
  ];

  // Band 2 is about the installs themselves: are they being used, and are they current.
  const healthTiles: Tile[] = [
    {
      key: 'active', label: 'Active (30 days)', value: st?.active_last_30d, tone: 'success', icon: <Wifi size={18} />, active: activity === 'active',
      hint: st?.active_last_7d != null ? `${st.active_last_7d} of them in the last 7 days` : 'seen in the last 30 days',
      onClick: st?.active_last_30d ? toggle(activity === 'active', { activity: 'active' }) : undefined,
    },
    {
      key: 'stale', label: 'Dormant 30 days+', value: st?.stale, tone: 'warning', icon: <MoonStar size={18} />, active: activity === 'stale',
      hint: st?.oldest_stale_days != null ? `the quietest has not opened it in ${st.oldest_stale_days}d` : 'nobody has gone quiet',
      onClick: st?.stale ? toggle(activity === 'stale', { activity: 'stale' }) : undefined,
    },
    {
      key: 'outdated', label: 'Running an old build', value: st?.outdated, tone: 'danger', icon: <CircleArrowUp size={18} />, active: version === '__outdated__',
      hint: latest ? `latest is v${latest}${st?.on_latest != null ? ` — ${st.on_latest} already on it` : ''}` : 'no app version reported yet',
      onClick: st?.outdated ? toggle(version === '__outdated__', { version: '__outdated__' }) : undefined,
    },
    {
      key: 'new', label: 'New in 30 days', value: st?.new_last_30d, tone: 'sky', icon: <Sparkles size={18} />, active: installed === 'new',
      hint: 'signed in for the very first time this month',
      onClick: st?.new_last_30d ? toggle(installed === 'new', { installed: 'new' }) : undefined,
    },
  ];

  /* ------------------------------------------------------- filter chips */

  const versionChipLabel = version === '__outdated__' ? 'behind the latest build'
    : version === '__unknown__' ? 'no version reported'
      : version === '__latest__' ? `v${latest}` : version ? `v${version.replace(/^v/, '')}` : '';
  const chips: { key: string; label: string; clear: () => void }[] = [
    ...(platform ? [{ key: 'platform', label: `Platform: ${platform === 'APP' ? 'Mobile app' : platform === 'UNKNOWN' ? 'Not recorded' : (PLATFORM_META[platform]?.label ?? platform)}`, clear: () => setPlatform('') }] : []),
    ...(version ? [{ key: 'version', label: `App version: ${versionChipLabel}`, clear: () => setVersion('') }] : []),
    ...(activity ? [{ key: 'activity', label: `Status: ${activity === 'active' ? 'Active' : activity === 'stale' ? 'Dormant' : activity === 'recent' ? 'Seen this week' : 'Never signed in'}`, clear: () => setActivity('') }] : []),
    ...(installed ? [{ key: 'installed', label: 'First seen: in the last 30 days', clear: () => setInstalled('') }] : []),
    ...(role ? [{ key: 'role', label: `Role: ${roleLabel(role)}`, clear: () => setRole('') }] : []),
    ...(from ? [{ key: 'from', label: `Seen from ${from.format('DD MMM YYYY')}`, clear: () => setFrom(null) }] : []),
    ...(to ? [{ key: 'to', label: `Seen up to ${to.format('DD MMM YYYY')}`, clear: () => setTo(null) }] : []),
    ...(qUrl ? [{ key: 'q', label: `Search: ${qUrl}`, clear: () => { setSearch(''); setQUrl(''); } }] : []),
  ];

  const roleOptions = useMemo(() => [
    { value: '', label: `All roles${st?.people != null ? ` (${st.people})` : ''}` },
    ...roleRows.map((r) => ({ value: r.role, label: `${roleLabel(r.role)} (${r.people})`, disabled: r.people === 0 })),
  ], [st]);


  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 type-page-title text-content">
            <MonitorSmartphone className="size-6 text-brand-600" /> App Installs &amp; Adoption
          </h1>
          <p className="mt-0.5 max-w-3xl text-sm text-muted">
            Who has a login, who actually opened the app, what build they are on and when they were last seen.
            Platform is reported by the app, or inferred from the browser's User-Agent (marked&nbsp;<span className="font-mono">~</span>).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {latest && (
            <div className="rounded-card border border-brand-500/40 bg-brand-50 px-3 py-1.5 dark:bg-brand-900/25">
              <div className="type-overline text-brand-700 dark:text-brand-300">Latest build</div>
              <div className="font-mono type-body-strong text-brand-800 dark:text-brand-200">v{latest}</div>
            </div>
          )}
          {st?.app != null && !!st?.people && (
            <div className="rounded-card border border-line bg-surface shadow-e1 px-3 py-1.5">
              <div className="type-overline text-subtle">App adoption</div>
              <div className="type-body-strong text-content">{pct(st.app, st.people)}% of {st.people}</div>
            </div>
          )}
        </div>
      </div>

      {/* KPI band 1 — where everyone with a login actually is. These five add up to "People". */}
      <div>
        <div className="mb-1.5 inline-flex items-center gap-1.5 type-overline text-subtle">
          <Users className="size-3" /> Where the {st?.people ?? '…'} people with a login are
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
          {reachTiles.map((t) => (
            <StatCard key={t.key} label={t.label} value={t.value ?? '—'} hint={t.hint} tone={t.tone} icon={t.icon} active={t.active} onClick={t.onClick} />
          ))}
        </div>
      </div>

      {/* KPI band 2 — how healthy those installs are. */}
      <div>
        <div className="mb-1.5 inline-flex items-center gap-1.5 type-overline text-subtle">
          <Activity className="size-3" /> How the installs are doing
        </div>
        <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
          {healthTiles.map((t) => (
            <StatCard key={t.key} label={t.label} value={t.value ?? '—'} hint={t.hint} tone={t.tone} icon={t.icon} active={t.active} onClick={t.onClick} />
          ))}
        </div>
      </div>

      {/* Breakdowns */}
      {/* `grid-cols-1` is load-bearing: without an explicit track the implicit column is `auto`, so it
          grows to the widest card's MIN-CONTENT (357px — a long device model name is `truncate`, and a
          nowrap run still counts its full width towards a flex row's min-content). That pushed the whole
          page 53px sideways at 320 and 13px at 360. `minmax(0,1fr)` caps the track at the container. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {/* Version adoption */}
        <Panel title="App version adoption" icon={<TagIcon className="size-3" />}
          hint={latest && st?.on_latest != null ? `${st.on_latest} of ${versionTotal} on v${latest}` : undefined}>
          <StackedBar className="mb-3 h-2.5" segments={versionRows.map((v, i) => ({
            key: v.version || 'unknown', value: v.count, label: v.version ? `v${v.version}` : 'not reported',
            cls: !v.version ? 'bg-subtle/55' : v.version === latest ? 'bg-brand-500' : i === 1 ? 'bg-harvest-400' : 'bg-warning-500',
          }))} />
          <div className="flex flex-col gap-1">
            {versionRows.map((v) => {
              const key = v.version || '__unknown__';
              const isLatest = !!v.version && v.version === latest;
              const active = version === key || (!!v.version && version === v.version);
              return (
                <button key={key} type="button" aria-pressed={active} disabled={!v.count}
                  onClick={() => drill(active ? {} : { version: key })}
                  className={cn('flex items-center gap-2 rounded-control border px-2 py-1.5 text-left transition',
                    active ? 'border-brand-600 bg-brand-500/10' : 'border-transparent hover:bg-surface-2',
                    !v.count && 'cursor-default opacity-60')}>
                  <span className={cn('size-2 shrink-0 rounded-pill', !v.version ? 'bg-subtle/55' : isLatest ? 'bg-brand-500' : 'bg-warning-500')} />
                  <span className={cn('font-mono type-caption-strong', v.version ? 'text-content' : 'italic text-muted')}>
                    {v.version ? `v${v.version}` : 'not reported'}
                  </span>
                  {isLatest && <span className="rounded-pill bg-brand-500/15 px-1.5 type-caption text-brand-700 dark:text-brand-300">latest</span>}
                  <span className="ml-auto type-caption-strong text-content">{v.count}</span>
                  <span className="w-9 text-right type-caption text-subtle">{pct(v.count, versionTotal)}%</span>
                </button>
              );
            })}
            {!versionRows.length && <div className="py-6 text-center type-caption text-subtle">No sessions yet.</div>}
          </div>
          {!!st?.unknown_version && (
            <div className="mt-auto pt-2 w-fit rounded-control bg-warning-500/10 px-2 py-1.5 type-caption text-warning-700 dark:text-harvest-300">
              {st.unknown_version} mobile {st.unknown_version === 1 ? 'install has' : 'installs have'} never reported a version — a build older than version reporting.
            </div>
          )}
        </Panel>

        {/* Devices */}
        <Panel title="Devices in the field" icon={<Smartphone className="size-3" />}
          hint={st ? `${deviceModels.length} model${deviceModels.length === 1 ? '' : 's'} seen` : undefined}>
          <div className="flex flex-col gap-2">
            {deviceModels.map((d) => (
              <div key={d.model} className="flex min-w-0 items-center gap-2">
                {/* No mark: a Smartphone on every device row repeats the panel's own title icon. */}
                <span className="min-w-0 flex-1 truncate type-caption-strong text-content" title={d.model}>{d.model}</span>
                <Tooltip label={`${d.count} of ${signedInTotal} sign-ins are on this model`}>
                  <span className="h-1.5 w-16 shrink-0 self-center overflow-hidden rounded-pill bg-surface-3">
                    <span className="block h-full rounded-pill bg-brand-500" style={{ width: `${pct(d.count, signedInTotal)}%` }} />
                  </span>
                </Tooltip>
                <span className="shrink-0 whitespace-nowrap type-caption-strong text-content">
                  {d.count} <span className="type-caption text-subtle">of {signedInTotal}</span>
                </span>
                <span className="w-9 shrink-0 text-right type-caption text-subtle">{pct(d.count, signedInTotal)}%</span>
              </div>
            ))}
            {!deviceModels.length && <div className="py-6 text-center type-caption text-subtle">{st && !st.devices ? 'This breakdown needs the matching API build.' : 'No device model has been reported yet.'}</div>}
          </div>
          {st?.devices != null && deviceless > 0 && (
            <button type="button" onClick={() => drill(platform === 'UNKNOWN' ? {} : { platform: 'UNKNOWN' })}
              className="mt-auto w-fit rounded-control bg-warning-500/10 px-2 py-1.5 text-left type-caption text-warning-700 hover:bg-warning-500/20 dark:text-harvest-300">
              {deviceless} of {st?.total} sign-ins name no device at all — mostly browsers and pre-reporting builds.
            </button>
          )}
        </Panel>

        {/* Adoption by role — the facet AND the answer to "which team is not on the app?" */}
        <Panel title="Adoption by role" icon={<Layers className="size-3" />} hint="on the app / people">
          {/* Without this a full-width blue bar beside "0 / 4" reads as 100% of something. */}
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 type-caption text-subtle">
            {[['bg-brand-500', 'on the app'], ['bg-sky-500', 'signed in, not on the app'], ['bg-danger-500/60', 'never signed in']].map(([cls, label]) => (
              <span key={label} className="inline-flex items-center gap-1"><span className={cn('size-2 rounded-pill', cls)} /> {label}</span>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            {roleRows.map((r) => {
              const active = role === r.role;
              const never = r.people - r.signedIn;
              const webOnly = r.signedIn - r.app;
              return (
                <button key={r.role} type="button" aria-pressed={active}
                  onClick={() => { setRole(active ? '' : r.role); clearUrl(['page']); revealResults(); }}
                  // One line per role — badge, meter, figures — on a fixed grid so the numbers
                  // line up down the panel. The three-line stack this replaces gave each role a
                  // full-panel-width bar that was a flat 880px of one colour for the five roles
                  // sitting at 0%, which read as decoration rather than data.
                  // Below `sm` the three tracks need ~346px and a phone panel has ~264px, so the meter
                  // drops under the badge and figures instead of pushing the page sideways (+81px at 320).
                  className={cn('grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-control border px-2 py-1 text-left transition sm:grid-cols-[7.5rem_minmax(56px,1fr)_auto] sm:gap-y-3',
                    active ? 'border-brand-600 bg-brand-500/10' : 'border-transparent hover:bg-surface-2')}>
                  <RoleBadge role={r.role} className="justify-self-start" />
                  <StackedBar className="order-last col-span-2 h-1.5 sm:order-0 sm:col-span-1" segments={[
                    { key: 'app', value: r.app, cls: 'bg-brand-500', label: 'on the app' },
                    { key: 'web', value: webOnly, cls: 'bg-sky-500', label: 'signed in, not on the app' },
                    { key: 'never', value: never, cls: 'bg-danger-500/60', label: 'never signed in' },
                  ]} />
                  <span className="flex items-center justify-end gap-2 whitespace-nowrap tabular-nums">
                    {!!never && (
                      <span className="rounded-pill bg-warning-500/12 px-1.5 py-px type-caption text-warning-700 dark:text-harvest-300"
                        title={`${never} of ${r.people} have never signed in`}>{never} unused</span>
                    )}
                    <span className="type-caption-strong text-content">{r.app} / {r.people}</span>
                    <span className="w-8 text-right type-caption text-subtle">{pct(r.app, r.people)}%</span>
                  </span>
                </button>
              );
            })}
            {!roleRows.length && <div className="py-6 text-center type-caption text-subtle">{st && !st.by_role ? 'This breakdown needs the matching API build.' : 'No users in this scope.'}</div>}
          </div>
        </Panel>

      </div>

      {/* Filters — in column order: who, role, then when they were last seen */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-line bg-surface shadow-e1 p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone…"
            className="w-full rounded-control border border-line bg-surface py-2 pl-9 pr-3 text-sm text-content outline-none focus:border-brand-500" />
        </div>
        <Select value={role} onChange={(v: string) => { setRole(v ?? ''); clearUrl(['page']); }} options={roleOptions} style={{ minWidth: 190 }} />
        {/* Wraps on a phone: two 148px date boxes plus the label and arrow are ~360px side by side and
            pushed the page 31px past a 320px viewport. Each box is left at its own width — narrowing a
            date field below ~148px clips the date it is showing. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="type-caption text-subtle">Last seen</span>
          <DatePicker value={from} onChange={(d) => { setFrom(d); clearUrl(['page']); }} placeholder="From" allowClear maxDate={to ?? dayjs()} style={{ width: 148 }} />
          <span className="text-subtle">→</span>
          <DatePicker value={to} onChange={(d) => { setTo(d); clearUrl(['page']); }} placeholder="To" allowClear minDate={from ?? undefined} maxDate={dayjs()} style={{ width: 148 }} />
        </div>
        <FilterResetButton size="md" onClick={reset} />
      </div>

      {/* Table */}
      <div ref={resultsRef} className="scroll-mt-20 rounded-card border border-line bg-surface shadow-e1">
        {/* A list narrowed from somewhere else on the page has to say so, and be undoable here. */}
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
          <span className="type-caption text-subtle">
            {pg.total ? `${pg.total} ${pg.total === 1 ? 'person' : 'people'}` : 'Nothing to show'}
            {chips.length ? ' · showing only' : ''}
          </span>
          {chips.map((c) => (
            <button key={c.key} type="button" onClick={c.clear}
              className="inline-flex items-center gap-1 rounded-pill border border-brand-500/40 bg-brand-500/10 px-2 py-0.5 type-caption text-brand-800 hover:bg-brand-500/20 dark:text-brand-200">
              {c.label} <span aria-hidden>✕</span><span className="sr-only">Remove this filter</span>
            </button>
          ))}
          {chips.length > 1 && (
            <button type="button" onClick={reset} className="type-caption text-muted underline-offset-2 hover:text-content hover:underline">Clear all</button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="gb-thead-tint">
              <tr className="bg-surface-2/60 text-left type-overline text-subtle">
                <th className="w-8 px-2 py-2.5" />
                <th className="px-3 py-2.5">User</th>
                <th className="px-3 py-2.5">Role</th>
                <th className="px-3 py-2.5">Platform &amp; device</th>
                <th className="px-3 py-2.5">App build</th>
                <th className="px-3 py-2.5">Logins</th>
                <th className="px-3 py-2.5">First seen</th>
                <th className="px-3 py-2.5">
                  <button type="button" onClick={() => setSort(sort === 'desc' ? 'asc' : 'desc')}
                    className="inline-flex items-center gap-1 type-overline text-subtle hover:text-content">
                    Last seen <ChevronDown className={cn('size-3 transition-transform', sort === 'asc' && 'rotate-180')} />
                  </button>
                </th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading ? (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-muted">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-12 text-center">
                  <div className="text-muted">No one matches {chips.length ? 'these filters' : 'this view'}.</div>
                  {chips.length > 0 && <FilterResetButton onClick={reset} className="mt-2" />}
                </td></tr>
              ) : rows.map((r) => {
                const isOpen = open.has(r.id);
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={cn('border-t border-line transition-colors hover:bg-surface-2/70',
                        // A gap gets a tint, not just a tag — a never-used login is the row someone
                        // is here to find, and it should be visible while scrolling past it.
                        r.activity === 'never' && 'bg-danger-500/[0.04]',
                        isOpen && 'bg-surface-2/60')}>
                      <td className="px-2 py-2.5 align-top">
                        <button type="button" aria-expanded={isOpen} aria-label={isOpen ? 'Hide device detail' : 'Show device detail'}
                          onClick={() => setOpen((s) => { const n = new Set(s); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })}
                          className="grid size-7 place-items-center rounded-control text-muted hover:bg-surface-3 hover:text-content">
                          <ChevronRight className={cn('size-4 transition-transform', isOpen && 'rotate-90')} />
                        </button>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <EntityAvatar name={r.name || r.phone || '?'} src={r.photoUrl} size={32} muted={r.activity === 'never'} />
                          <div className="min-w-0">
                            <div className="truncate type-body-medium text-content">{r.name || '—'}</div>
                            {r.phone
                              ? <a href={`tel:${r.phone}`} className="mt-0.5 inline-flex items-center gap-1 rounded bg-brand-50 px-1.5 py-px font-mono type-caption text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300"><Phone className="size-2.5" />{r.phone}</a>
                              : <div className="type-caption text-warning-700 dark:text-harvest-300">no number on file</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-top"><RoleBadge role={r.role} /></td>
                      <td className="px-3 py-2.5 align-top">
                        <PlatformChip platform={r.platform} inferred={r.platformInferred} />
                        <div className="mt-0.5 max-w-[180px] truncate type-caption text-muted" title={r.deviceModel ?? undefined}>
                          {r.deviceModel || <span className="italic text-subtle">device not named</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-top"><VersionCell row={r} latest={latest} /></td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="type-body-medium text-content">{r.sessionCount || '—'}</div>
                        <div className="type-caption text-subtle">{r.deviceCount ? `${r.deviceCount} device${r.deviceCount === 1 ? '' : 's'}` : 'no device id'}</div>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {r.firstSeen
                          ? <><div className="type-caption text-content">{formatIstDate(r.firstSeen)}</div><div className="type-caption text-subtle">{relTime(r.firstSeen)}</div></>
                          : <span className="type-caption italic text-subtle">never</span>}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {r.lastSeen
                          ? <><div className="type-body-medium text-content">{relTime(r.lastSeen)}</div><div className="type-caption text-subtle">{formatIst(r.lastSeen)}</div></>
                          : <span className="type-caption italic text-subtle">never</span>}
                      </td>
                      <td className="px-3 py-2.5 align-top"><ActivityTag activity={r.activity} lastSeen={r.lastSeen} /></td>
                    </tr>
                    {isOpen && (
                      <ExpandedRow colSpan={9}>
                        <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                          <Row label="Device model" value={r.deviceModel} />
                          <Row label="Device id" value={r.deviceId ? <span className="font-mono type-caption">{r.deviceId}</span> : null} />
                          <Row label="Last IP" value={r.ip ? <span className="font-mono type-caption">{r.ip}</span> : null} />
                          <Row label="Last login" value={r.lastLoginAt ? formatIst(r.lastLoginAt) : null} />
                          <Row label="Account created" value={r.accountCreatedAt ? formatIstDate(r.accountCreatedAt) : null} />
                          <Row label="First sign-in" value={r.firstSeen ? formatIst(r.firstSeen) : 'never'} />
                          <Row label="Sign-ins recorded" value={`${r.sessionCount} on ${r.deviceCount} device${r.deviceCount === 1 ? '' : 's'}`} />
                          <Row label="App build" value={r.appVersion ? `v${r.appVersion.replace(/^v/, '')}${r.versionStatus === 'outdated' ? ` (latest is v${latest})` : ''}` : 'not reported'} />
                          <div className="sm:col-span-2 lg:col-span-4">
                            <Row label="User agent" value={r.userAgent ? <span className="font-mono type-caption text-muted">{r.userAgent}</span> : 'none recorded — this session came from a client that sent no User-Agent'} />
                          </div>
                        </div>
                      </ExpandedRow>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-line px-3 py-2 type-caption text-muted">
          <div>{pg.total ? `Showing ${(pg.page - 1) * PAGE_SIZE + 1}–${Math.min(pg.page * PAGE_SIZE, pg.total)} of ${pg.total}` : '0 people'}</div>
          <div className="inline-flex items-center gap-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={pg.page <= 1 || listQuery.isFetching}
              className="inline-flex items-center gap-1 rounded-control border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-50"><ChevronLeft className="size-3.5" /> Prev</button>
            <span>Page {pg.page} / {pg.pages || 1}</span>
            <button onClick={() => setPage(page + 1)} disabled={pg.page >= (pg.pages || 1) || listQuery.isFetching}
              className="inline-flex items-center gap-1 rounded-control border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-50">Next <ChevronRight className="size-3.5" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
